import { useCallback, useEffect } from 'react';
import {
  NavigateOptions,
  URLSearchParamsInit,
  useSearchParams,
} from 'react-router-dom';

export type SetSearchParamsOverride = (
  nextInit?:
    | URLSearchParamsInit
    | ((prev: URLSearchParams) => URLSearchParamsInit),
  navigateOpts?: NavigateOptions
) => void;

// Overload for when defaultValue is provided (returns non-null value)
export function useStateFromSearchParams(
  paramName: string,
  defaultValue: string,
  setSearchParamsOverride?: SetSearchParamsOverride
): [string, (newValue: string | null) => void];

// Overload for when defaultValue is null or not provided (may return null)
export function useStateFromSearchParams(
  paramName: string,
  defaultValue?: null,
  setSearchParamsOverride?: SetSearchParamsOverride
): [string | null, (newValue: string | null) => void];

export function useStateFromSearchParams(
  paramName: string,
  defaultValue: string | null = null,
  // When called within the same render, multiple calls to setSearchParams will override each other if they are not from the same useSearchParams hook
  setSearchParamsOverride?: SetSearchParamsOverride
) {
  const [searchParams, setSearchParamsLocal] = useSearchParams();
  const setSearchParams = setSearchParamsOverride || setSearchParamsLocal;
  const paramValue = searchParams.get(paramName);
  const setParam = useCallback(
    (newValue: string | null) => {
      if (newValue) {
        setSearchParams(
          (prev) => {
            prev.set(paramName, newValue);
            return prev;
          },
          { replace: true }
        );
      } else {
        setSearchParams(
          (prev) => {
            prev.delete(paramName);
            return prev;
          },
          { replace: true }
        );
      }
    },
    [paramName, setSearchParams]
  );
  useEffect(() => {
    if (!paramValue && defaultValue) {
      setParam(defaultValue);
    }
  }, [defaultValue, paramValue, setParam]);

  return [paramValue, setParam] as const;
}
